import { Button, Textarea, cn } from "@nexus/ui";
import { useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** Mirrors `LONG_TEXT_MAX_LENGTH` on `AddOrderScriptPreflightCommentSchema`. */
export const PREFLIGHT_COMMENT_BODY_MAX = 2_000;
const PREFLIGHT_COMMENT_COUNTER_REVEAL = 1_600;
const PREFLIGHT_COMMENT_COUNTER_TIGHT = 80;
const COMPOSER_MIN_PX = 40;
const COMPOSER_MAX_PX = 176;

interface DetailCommentComposerProps {
  disabled?: boolean;
  pending?: boolean;
  viewer?: "client" | "partner";
  onSubmit: (body: string) => void | Promise<void>;
}

export function DetailCommentComposer({
  disabled = false,
  pending = false,
  viewer = "client",
  onSubmit,
}: DetailCommentComposerProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const hintId = useId();
  const counterId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const trimmed = draft.trim();
  const used = draft.length;
  const remaining = PREFLIGHT_COMMENT_BODY_MAX - used;
  const showCounter = used >= PREFLIGHT_COMMENT_COUNTER_REVEAL;
  const atLimit = remaining <= 0;
  const isBusy = disabled || pending || isSubmitting;
  const canSend = trimmed.length > 0 && !isBusy;
  const sharedHint =
    viewer === "partner"
      ? t(
          "marketplace.partner.workspace.commentSharedHint",
          "Visible to the client",
        )
      : t(
          "marketplace.orders.list.panel.commentSharedHint",
          "Visible to the production partner",
        );
  const placeholder = t(
    "marketplace.orders.list.panel.commentPlaceholder",
    "Write a note…",
  );

  useLayoutEffect(() => {
    const node = textareaRef.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${Math.min(
      Math.max(node.scrollHeight, COMPOSER_MIN_PX),
      COMPOSER_MAX_PX,
    )}px`;
  }, [draft]);

  return (
    <form
      className="sticky bottom-0 z-10 mt-auto shrink-0 border-t border-border bg-muted px-4 py-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSend) return;
        void (async () => {
          setIsSubmitting(true);
          try {
            await onSubmit(trimmed);
            setDraft("");
          } catch {
            // Mutation hooks own the toast.
          } finally {
            setIsSubmitting(false);
          }
        })();
      }}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-3">
          <label
            htmlFor={inputId}
            className="text-caption font-medium text-foreground"
          >
            {t("marketplace.orders.list.panel.commentLabel", "Add a note")}
          </label>
          <p id={hintId} className="text-caption text-muted-foreground">
            {sharedHint}
          </p>
        </div>
        <div
          className={cn(
            "flex flex-col rounded-sm border border-border bg-background transition-[border-color,box-shadow] duration-150",
            "focus-within:border-input-focus-border focus-within:ring-[3px] focus-within:ring-ring/20",
          )}
        >
          <Textarea
            ref={textareaRef}
            id={inputId}
            rows={1}
            value={draft}
            disabled={isBusy}
            maxLength={PREFLIGHT_COMMENT_BODY_MAX}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || (!event.metaKey && !event.ctrlKey))
                return;
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }}
            aria-keyshortcuts="Meta+Enter Control+Enter"
            aria-describedby={
              showCounter ? `${hintId} ${counterId}` : hintId
            }
            placeholder={placeholder}
            className="text-drawer-body min-h-10 resize-none overflow-y-auto border-0 bg-transparent px-3 py-2 shadow-none focus-visible:border-transparent focus-visible:ring-0"
          />
          <div className="flex items-center justify-end gap-2 px-2 pb-2">
            {showCounter ? (
              <span
                id={counterId}
                className={cn(
                  "me-auto text-[10px] leading-none shrink-0 tabular-nums tracking-tight transition-colors duration-150",
                  atLimit
                    ? "text-warning"
                    : remaining <= PREFLIGHT_COMMENT_COUNTER_TIGHT
                      ? "text-foreground"
                      : "text-muted-foreground",
                )}
                aria-live="polite"
                aria-label={
                  atLimit
                    ? t("marketplace.orders.list.panel.commentAtLimitAria", {
                        max: PREFLIGHT_COMMENT_BODY_MAX,
                        defaultValue:
                          "Character limit reached ({{max}} characters)",
                      })
                    : t("marketplace.orders.list.panel.commentUsedOfMaxAria", {
                        used,
                        max: PREFLIGHT_COMMENT_BODY_MAX,
                        defaultValue: "{{used}} of {{max}} characters",
                      })
                }
              >
                {t("marketplace.orders.list.panel.commentUsedOfMax", {
                  used,
                  max: PREFLIGHT_COMMENT_BODY_MAX,
                  defaultValue: "{{used}} / {{max}}",
                })}
              </span>
            ) : (
              <kbd
                className="rounded-sm border border-border px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground"
                aria-hidden
              >
                {t("marketplace.orders.list.panel.commentShortcut", "⌘↵")}
              </kbd>
            )}
            <Button
              type="submit"
              size="sm"
              variant={canSend ? "default" : "ghost"}
              disabled={!canSend}
            >
              {pending || isSubmitting
                ? t("marketplace.orders.list.panel.commentSending", "Sending…")
                : t("marketplace.orders.list.panel.commentSend", "Comment")}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
